import { useState } from 'react';
import { Box, Button, Stack, Tab, Tabs, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { useConfirm } from 'material-ui-confirm';
import { formatRelative } from 'date-fns';
import ShareIcon from '@mui/icons-material/Share';
import InsightsIcon from '@mui/icons-material/Insights';
import { useUserContext } from '@/contexts/userContext';
import { useApiContext } from '@/contexts/apiContext';
import ErrorMessage from '@/components/errorMessage';
import ProgressIndicator from '@/components/progressIndicator';
import TabPanel from '@/components/tabPanel';
import EntityAccessControl from '../entities/administration/accessControl/accessControl';
import AuditRecords from '../entities/administration/auditRecords/auditRecords';
import UsageStats from './usageStats';

type Props = { dataSourceInstance: any; isOpen: boolean; maxHeight: number };

const DataSourceInstanceAdministration = ({ dataSourceInstance = {}, isOpen, maxHeight }: Props) => {
  const { t } = useTranslation();
  const { hasPermission } = useUserContext();
  const navigate = useNavigate();
  const location = useLocation();
  const apiContext = useApiContext();
  const confirm = useConfirm();

  const entityBase = `${dataSourceInstance?.entityType}:${dataSourceInstance?.entityDef}`;

  const canDelete = hasPermission(`pipeline:dataSources:delete`, dataSourceInstance?.userAccess);

  const [selectedTab, setSelectedTab] = useState<any>(
    hasPermission(`pipeline:dataSources:accessControl:view`, dataSourceInstance?.userAccess)
      ? 'accessControl'
      : hasPermission(`pipeline:dataSources:usageStats:view`, dataSourceInstance?.userAccess)
        ? 'usageStats'
        : 'auditTrail',
  );

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<any>();

  const handleTabChange = (event: React.SyntheticEvent, newValue: string) => {
    setSelectedTab(newValue);
  };

  const deleteDataSourceInstance = async () => {
    confirm({ description: 'Are you sure you want to delete this data source?' }).then(async () => {
      try {
        setLoading(true);
        await apiContext.deleteDataSourceInstance(dataSourceInstance.entityDefId, dataSourceInstance.id);
        const from = location.state?.from || '/instances?tab=dataSourceInstances';
        navigate(from);
      } catch (error) {
        setError(error);
      }
      setLoading(false);
    });
  };

  if (loading) {
    return <ProgressIndicator />;
  }

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <Box sx={{ flexShrink: 0 }}>
        {error && <ErrorMessage error={error} />}

        {/* Metadata Header */}
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{
            px: 2,
            py: 1.25,
            border: 1,
            borderColor: 'divider',
            borderRadius: '12px',
            mb: 1,
            bgcolor: 'background.paper',
          }}
        >
          <Stack direction="row" spacing={3} alignItems="center">
            <Typography variant="caption" sx={{ fontWeight: 600 }}>
              Administration ({dataSourceInstance?.name})
            </Typography>
            {dataSourceInstance?.createdDate && (
              <Typography variant="caption" color="text.secondary">
                Created: {formatRelative(new Date(dataSourceInstance.createdDate), new Date())}
              </Typography>
            )}
            {dataSourceInstance?.updatedDate && (
              <Typography variant="caption" color="text.secondary">
                Updated: {formatRelative(new Date(dataSourceInstance.updatedDate), new Date())}
              </Typography>
            )}
          </Stack>
          {canDelete && (
            <Button
              variant="text"
              size="small"
              color="error"
              onClick={deleteDataSourceInstance}
              sx={{ textTransform: 'none', fontWeight: 600 }}
            >
              Delete Data Source
            </Button>
          )}
        </Stack>

        {/* Tabs Bar */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider', bgcolor: 'background.paper' }}>
          <Tabs value={selectedTab} onChange={handleTabChange} indicatorColor="secondary" sx={{ minHeight: 40 }}>
            {hasPermission(`pipeline:dataSources:accessControl:view`, dataSourceInstance?.userAccess) && (
              <Tab
                value="accessControl"
                icon={<ShareIcon sx={{ fontSize: 18 }} />}
                iconPosition="start"
                label={
                  <Typography variant="caption" sx={{ fontWeight: selectedTab === 'accessControl' ? 700 : 500 }}>
                    Access Control
                  </Typography>
                }
                sx={{ minHeight: 40, textTransform: 'none' }}
              />
            )}
            {hasPermission(`pipeline:dataSources:usageStats:view`, dataSourceInstance?.userAccess) && (
              <Tab
                value="usageStats"
                icon={<InsightsIcon sx={{ fontSize: 18 }} />}
                iconPosition="start"
                label={
                  <Typography variant="caption" sx={{ fontWeight: selectedTab === 'usageStats' ? 700 : 500 }}>
                    Usage Stats
                  </Typography>
                }
                sx={{ minHeight: 40, textTransform: 'none' }}
              />
            )}
            {hasPermission(`pipeline:dataSources:auditTrail:view`, dataSourceInstance?.userAccess) && (
              <Tab
                value="auditTrail"
                icon={<InsightsIcon sx={{ fontSize: 18 }} />}
                iconPosition="start"
                label={
                  <Typography variant="caption" sx={{ fontWeight: selectedTab === 'auditTrail' ? 700 : 500 }}>
                    Audit Trail
                  </Typography>
                }
                sx={{ minHeight: 40, textTransform: 'none' }}
              />
            )}
          </Tabs>
        </Box>
      </Box>

      {/* Content Area */}
      <Box sx={{ flex: 1, overflow: 'hidden', mt: 1 }}>
        {isOpen && (
          <>
            {hasPermission(`pipeline:dataSources:accessControl:view`, dataSourceInstance?.userAccess) && (
              <TabPanel value="accessControl" currentTab={selectedTab} sx={{ height: '100%', overflow: 'hidden' }}>
                <EntityAccessControl entity={dataSourceInstance} />
              </TabPanel>
            )}
            {hasPermission(`pipeline:dataSources:usageStats:view`, dataSourceInstance?.userAccess) && (
              <TabPanel value="usageStats" currentTab={selectedTab} sx={{ height: '100%', overflow: 'hidden' }}>
                <UsageStats entity={dataSourceInstance} maxHeight={maxHeight} />
              </TabPanel>
            )}
            {hasPermission(`pipeline:dataSources:auditTrail:view`, dataSourceInstance?.userAccess) && (
              <TabPanel value="auditTrail" currentTab={selectedTab} sx={{ height: '100%', overflow: 'hidden' }}>
                <AuditRecords entity={dataSourceInstance} />
              </TabPanel>
            )}
          </>
        )}
      </Box>
    </Box>
  );
};

export default DataSourceInstanceAdministration;
